import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Shared state and handlers for Add/Edit form pages.
 *
 * @param {string|undefined} id            - route param; undefined = create mode
 * @param {string}           backPath      - where to navigate after save/cancel/archive
 * @param {Function}         [archiveFn]   - (id) => Promise; omit if page has no archive
 * @param {Function}         [unarchiveFn] - (id) => Promise; omit if page has no restore
 * @param {string}           [newLabel]    - page title in create mode (default 'New record')
 */
export default function useEntityForm({ id, backPath, archiveFn, unarchiveFn, newLabel = 'New record' }) {
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isArchived, setIsArchived] = useState(false);
  const [pageTitle, setPageTitle] = useState(isEdit ? 'Loading…' : newLabel);

  const handleCancel = useCallback(() => navigate(backPath), [navigate, backPath]);

  const handleArchive = useCallback(async () => {
    if (!archiveFn || !id) return;
    setIsSubmitting(true);
    try {
      await archiveFn(id);
      navigate(backPath);
    } catch (err) {
      setFormError(err.message || 'Failed to archive. Please try again.');
      setIsSubmitting(false);
    }
  }, [id, archiveFn, backPath, navigate]);

  // confirmMessage is built by the caller (has access to entity name in local state)
  const handleRestore = useCallback(async (confirmMessage) => {
    if (!unarchiveFn || !id) return;
    if (!window.confirm(confirmMessage)) return;
    setIsSubmitting(true);
    try {
      await unarchiveFn(id);
      navigate(backPath);
    } catch (err) {
      setFormError(err.message || 'Failed to restore. Please try again.');
      setIsSubmitting(false);
    }
  }, [id, unarchiveFn, backPath, navigate]);

  // Wraps the entity-specific API call in the standard try/catch/finally + navigate.
  // Pass an optional onError(err) to handle errors differently (e.g. BlockFormPage's
  // affectedBookings logic). When onError is omitted, sets formError from err.message.
  const runSubmit = useCallback(async (apiFn, onError) => {
    setIsSubmitting(true);
    try {
      await apiFn();
      navigate(backPath);
    } catch (err) {
      if (onError) {
        onError(err);
      } else {
        setFormError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [navigate, backPath]);

  return {
    isEdit,
    isBusy: isSubmitting || isLoading,
    navigate,
    formError,
    setFormError,
    isSubmitting,
    setIsSubmitting,
    isLoading,
    setIsLoading,
    isArchived,
    setIsArchived,
    pageTitle,
    setPageTitle,
    handleCancel,
    handleArchive,
    handleRestore,
    runSubmit,
  };
}
